import { useEffect, useState, useCallback } from 'react';

export type Theme = 'light' | 'dark' | 'cyan' | 'system';

const THEME_STORAGE_KEY = 'theme';

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'cyan' || stored === 'system') {
    return stored;
  }
  return 'dark';
}

function applyTheme(theme: Theme, withTransition = false) {
  const root = document.documentElement;
  const effectiveTheme = theme === 'system' ? getSystemTheme() : theme;

  // Add transition class for smooth theme switching
  if (withTransition) {
    root.classList.add('transitioning');
    setTimeout(() => {
      root.classList.remove('transitioning');
    }, 300);
  }

  // Remove all theme classes first
  root.classList.remove('dark', 'cyan');

  // Apply the appropriate theme class
  if (effectiveTheme === 'dark') {
    root.classList.add('dark');
  } else if (effectiveTheme === 'cyan') {
    root.classList.add('cyan');
  }
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);
  const [isFirstRender, setIsFirstRender] = useState(true);

  // Apply theme on mount and when theme changes
  useEffect(() => {
    // Only apply transition after first render (not on mount)
    applyTheme(theme, !isFirstRender);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    if (isFirstRender) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsFirstRender(false);
    }
  }, [theme, isFirstRender]);

  // Listen for system theme changes when using 'system' theme
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => applyTheme('system');

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      // toggleTheme is for light/dark switching only
      // If current theme is cyan or system, toggle to light/dark cycle
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'light';
      if (prev === 'system') {
        // If system, toggle to opposite of current system preference
        return getSystemTheme() === 'dark' ? 'light' : 'dark';
      }
      // If cyan, start with light
      return 'light';
    });
  }, []);

  const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;

  return {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
  };
}
